import { AssetTreePicker, ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import {
	Box,
	createStyles,
	Grid,
	makeStyles,
	Typography,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHandleTables } from "./components/hooks/useHandleTables";
import { useInitialValues } from "./components/hooks/useInitialValues";
import { useSearchParamsToReducer } from "./components/hooks/useSearchParamsToReducer";
import SplitScreen from "./components/SplitScreen";
import { useSearchStatistics } from "./components/statistics/hooks/useSearchStatistics";
import { useAssetDetailsContext } from "./context/assetDetailsContext";
import { useInitializeAssetDetails } from "./hooks/useInitializeAssetDetails";
import "./styles.css";

const useStyles = makeStyles((theme) =>
	createStyles({
		contentWrapper: {
			height: "auto",
			padding: theme.spacing(2),
		},
		headerContainer: {
			marginBottom: theme.spacing(1),
			minHeight: "60px",
		},
		assetTitle: {
			margin: "-8px 0 0px",
			fontWeight: 600,
			userSelect: "none",
			textTransform: "uppercase",
			fontSize: "1.4rem !important",
			[theme.breakpoints.down("sm")]: {
				fontSize: "1.1rem",
				textAlign: "center",
			},
		},
		assetPath: {
			margin: 0,
			fontWeight: 400,
			color: theme.palette.text.secondary,
			fontSize: "0.7rem",
			lineHeight: 1.1,
			opacity: 0.8,
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis",
			[theme.breakpoints.down("sm")]: {
				textAlign: "center",
				fontSize: "0.65rem",
			},
		},
		treePickerContainer: {
			[theme.breakpoints.down("sm")]: {
				marginTop: theme.spacing(1),
			},
		},
		buttonsContainer: {
			[theme.breakpoints.down("sm")]: {
				marginTop: theme.spacing(1),
				justifyContent: "center",
			},
			[theme.breakpoints.up("md")]: {
				justifyContent: "flex-end",
			},
		},
		buttonGroup: {
			width: 250,
			[theme.breakpoints.down("sm")]: {
				width: "100%",
				maxWidth: 300,
			},
		},
		resourceButton: {
			padding: "3px 16px",
			borderRadius: 0,
			borderTopLeftRadius: 3,
			textDecoration: "none",
			margin: 0,
			fontSize: "0.575rem",
		},
		statisticsButton: {
			padding: "3px 16px",
			borderRadius: 0,
			borderTopRightRadius: 3,
			textDecoration: "none",
			margin: 0,
			fontSize: "0.575rem",
		},
		workOrdersButton: {
			padding: "3px 16px",
			borderRadius: 0,
			borderBottomRightRadius: 3,
			borderBottomLeftRadius: 3,
			textDecoration: "none",
			margin: 0,
			fontSize: "0.575rem",
		},
		mainContent: {
			height: "calc(100vh - 160px)",
		},
	}),
);

export const AssetDetails = () => {
	const classes = useStyles();
	const isMountRef = useRef(true);
	const navigate = useNavigate();
	const {
		state: {
			assetSearchId,
			assetSearchName,
			statisticsTable,
			workOrdersTable,
			assetsTable,
			currentGridName,
			assetFullPath,
		},
	} = useAssetDetailsContext();
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");
	const [isStatisticsLoading, setIsStatisticsLoading] = useState(false);
	const [statisticsError, setStatisticsError] = useState("");

	const getCompactPath = (fullPath: string) => {
		if (!fullPath) return "Loading path...";
		const parts = fullPath.split("/");
		if (parts.length <= 3) return fullPath;
		return `${parts[0]}/.../${parts[parts.length - 2]}/${
			parts[parts.length - 1]
		}`;
	};

	const changeAsset = (value: number | null) => {
		navigate(
			`/asset-details/${value}?resource=${currentGridName}&assets=true&statistics=true&workOrders=true`,
			{ replace: true },
		);
	};

	const searchStatistics = useSearchStatistics({
		setIsStatisticsLoading,
		setStatisticsError,
	});

	useInitialValues();
	useSearchParamsToReducer();
	useInitializeAssetDetails({ setIsLoading, setError });

	const [handleAssetsTable, handleStatisticsTable, handleWorkOrdersTable] =
		useHandleTables();

	useEffect(() => {
		// Only search if we have a valid assetSearchId (not 0, undefined, or null)
		// and it's not the first mount
		if (!isMountRef.current && assetSearchId && assetSearchId !== 0) {
			searchStatistics();
		}
		isMountRef.current = false;
	}, [assetSearchId]);

	return (
		<>
			<HelmetDexteel title="Asset Details" />
			<Box className={classes.contentWrapper}>
				<Grid
					container
					className={classes.headerContainer}
					alignItems="center"
					spacing={2}
				>
					<Grid item xs={12} md={4}>
						<Box>
							<Grid item xs={12}>
								<Typography variant="caption" className={classes.assetPath}>
									{getCompactPath(assetFullPath as string)}
								</Typography>
							</Grid>
							<Typography
								variant="h6"
								className={classes.assetTitle}
								title={assetFullPath || "Loading path..."}
							>
								{assetSearchName}
							</Typography>
						</Box>
					</Grid>

					<Grid item xs={12} md={4} className={classes.treePickerContainer}>
						<AssetTreePicker
							label="Asset"
							value={Number.parseInt(assetSearchId as string) || 0}
							onChange={changeAsset}
						/>
					</Grid>

					<Grid item xs={12} md={4}>
						<Grid container className={classes.buttonsContainer}>
							<Grid item>
								<Box className={classes.buttonGroup}>
									<Grid container>
										<Grid item xs={6} onClick={handleAssetsTable}>
											<Button
												fullWidth
												variant="contained"
												color={
													assetsTable && statisticsTable && workOrdersTable
														? "default"
														: assetsTable
															? "primary"
															: "default"
												}
												className={classes.resourceButton}
											>
												RESOURCES
											</Button>
										</Grid>
										<Grid item xs={6} onClick={handleStatisticsTable}>
											<Button
												fullWidth
												variant="contained"
												color={
													assetsTable && statisticsTable && workOrdersTable
														? "default"
														: statisticsTable
															? "primary"
															: "default"
												}
												className={classes.statisticsButton}
											>
												STATISTICS
											</Button>
										</Grid>
										<Grid item xs={12} onClick={handleWorkOrdersTable}>
											<Button
												fullWidth
												variant="contained"
												color={
													assetsTable && statisticsTable && workOrdersTable
														? "default"
														: workOrdersTable
															? "primary"
															: "default"
												}
												className={classes.workOrdersButton}
											>
												WORK ORDERS
											</Button>
										</Grid>
									</Grid>
								</Box>
							</Grid>
						</Grid>
					</Grid>
				</Grid>

				<Box className={classes.mainContent}>
					<SplitScreen
						searchStatistics={searchStatistics}
						isStatisticsLoading={isStatisticsLoading}
					/>
				</Box>

				<ErrorModal error={error} onHide={() => setError("")} />
				<ErrorModal
					error={statisticsError}
					onHide={() => setStatisticsError("")}
				/>
			</Box>
		</>
	);
};
